import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import { revmacTheme } from './theme/revmacTheme';
import { AuthProvider } from './contexts/AuthContext';
import { PracticeProvider } from './contexts/PracticeContext';
import Layout from './components/Layout';
import LoginForm from './components/LoginForm';
import Claims from './pages/Claims';
import Upload from './pages/Upload';
import Triage from './pages/Triage';
import Status from './pages/Status';
import AuditLog from './pages/AuditLog';
import EobArchive from './pages/EobArchive';
import Billers from './pages/Billers';
import BillerDetail from './pages/BillerDetail';
import Practices from './pages/Practices';
import PracticeDetail from './pages/PracticeDetail';
import Providers from './pages/Providers';
import {
  getSession,
  signOut as cognitoSignOut,
  getUserFromSession,
  AuthTokens,
  CognitoAuthUser,
} from './services/cognitoAuth';

function App() {
  const [user, setUser] = useState<CognitoAuthUser | null>(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    getSession()
      .then((tokens) => {
        const authUser = getUserFromSession(tokens);
        setUser(authUser);
      })
      .catch(() => {
        setUser(null);
      })
      .finally(() => {
        setIsCheckingAuth(false);
      });
  }, []);

  const handleAuthenticated = useCallback((authUser: CognitoAuthUser, _tokens: AuthTokens) => {
    setUser(authUser);
  }, []);

  const handleSignOut = useCallback(() => {
    cognitoSignOut();
    setUser(null);
  }, []);

  return (
    <ThemeProvider theme={revmacTheme}>
      <CssBaseline />
      <Router>
        {isCheckingAuth ? null : !user ? (
          <LoginForm onAuthenticated={handleAuthenticated} />
        ) : (
          <AuthProvider user={user}>
            <PracticeProvider>
              <Layout user={user} signOut={handleSignOut}>
                <Routes>
                  <Route index element={<Navigate to="/upload" replace />} />
                  <Route path="claims"                   element={<Claims />} />
                  <Route path="upload"                   element={<Upload />} />
                  <Route path="triage"                   element={<Triage />} />
                  {/* Back-compat: old per-upload triage links now land on the
                      practice-wide Triage view. The uploadId is dropped on
                      navigation - operators who want per-upload scoping use
                      Claims with the upload filter. */}
                  <Route path="uploads/:uploadId/triage" element={<Navigate to="/triage" replace />} />
                  <Route path="status"                   element={<Status />} />
                  <Route path="audit-events"             element={<AuditLog />} />
                  <Route path="eobs"                     element={<EobArchive />} />
                  <Route path="billers"                  element={<Billers />} />
                  <Route path="billers/:id"              element={<BillerDetail />} />
                  <Route path="practices"                element={<Practices />} />
                  <Route path="practices/:id"            element={<PracticeDetail />} />
                  <Route path="providers"                element={<Providers />} />
                  <Route path="*"                        element={<Navigate to="/upload" replace />} />
                </Routes>
              </Layout>
            </PracticeProvider>
          </AuthProvider>
        )}
      </Router>
    </ThemeProvider>
  );
}

export default App;
